/**
 * receivables — every vertical. GET /invoices/summary/dashboard, as sent.
 *
 * FOUR FIGURES THAT PARTITION THE OPEN BOOK (api#881, api#884). Every unpaid,
 * unvoided invoice is in exactly one of them, so they add up to what is owed
 * with nothing counted twice:
 *
 *   Overdue            balance_overdue    sent, and past its due DAY in the
 *                                          tenant's zone. Derived at read time
 *                                          by the API; nothing writes it.
 *   Sent, not yet due  balance_sent
 *   Saved, not sent    balance_finalized  finalized — payable, but no customer
 *                                          has been asked for it yet
 *   Drafts             balance_draft
 *
 * The old "Outstanding" figure (balance_outstanding = sent + overdue) is gone:
 * beside Overdue it counted the overdue money twice.
 *
 * PAID THIS MONTH (paid_this_month, the tenant's calendar month) is money IN,
 * not part of what is owed, so it sits on a footer line under a divider rather
 * than as a fifth figure the eye would add to the four.
 *
 * PAST DUE ON THE SAVED FIGURE. finalized_past_due_count / _balance are a
 * SUBSET of balance_finalized — saved invoices whose due day has passed, which
 * should already have gone out. It is a note under that figure, never a figure
 * of its own: a second red number beside Overdue would read as more money
 * owed. Amber, not red, so red stays the one thing a customer has been asked
 * for and not paid. Hidden at zero, and hidden when the API did not send it —
 * an absent count is not "0 past due".
 *
 * Every figure is the API's. Its money columns are COALESCE(SUM(...), 0), so
 * "$0.00" here is the API saying zero; a key it did not send is "—". A refused
 * read (the route admits admin, office staff, supervisors and dispatchers) is a
 * refusal, not an empty ledger.
 *
 * EVERY OTHER VERTICAL: GET /dashboard/receivables (api#1121). Casing alone
 * keeps the read above and its page is unchanged. A company with any other
 * vertical reads the global route instead — its casing figures are that
 * summary's own, plus every other vertical's — and the same four figures and
 * footer are drawn from its `total`:
 *
 *   - one entry per currency, side by side, never summed across them;
 *   - `saved_past_due` is still a SUBSET of `saved_not_sent`, so it is still
 *     the amber note and never a figure (trucking, downhole and PT 'approved'
 *     invoices count as saved, by the API's rule);
 *   - a vertical that is not `ok` is named under the figures
 *     (MoneyVerticalNotes), and its money is in none of them;
 *   - the link is Finance → Invoices, the list that holds every vertical's.
 */
import type { ReactNode } from 'react';
import { fmtCurrency, parseMoney } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';
import { parseCount } from '../buildActionAlerts';
import { useOverviewInvoiceDash, useReceivables } from '../overviewQueries';
import { useAuth } from '../../../contexts/AuthContext';
import { fmtAmounts } from '../../../lib/currency-amounts';
import type { Receivables, ReceivablesBucket } from '../../../types/overview-money';
import { MoneyVerticalNotes } from '../MoneyVerticalNotes';
import { hasGlobalMoneyVertical } from '../globalMoney';

function invoices(n: number | null): string {
  if (n === null) return '—';
  return `${n} ${n === 1 ? 'invoice' : 'invoices'}`;
}

/** The amber note, or null when there is nothing past due (or no count). */
export function pastDueNote(d: Record<string, unknown>): string | null {
  const pastDue = parseCount(d.finalized_past_due_count);
  if (pastDue === null || pastDue <= 0) return null;
  const saved = parseCount(d.finalized_count);
  if (saved !== null && pastDue === saved) return pastDue === 1 ? 'past due' : 'all past due';
  return `${pastDue} past due · ${fmtCurrency(parseMoney(d.balance_finalized_past_due))}`;
}

interface FiguresProps {
  overdue: string;
  overdueCount: number | null;
  sent: string;
  sentCount: number | null;
  saved: string;
  savedCount: number | null;
  note: string | null;
  draft: string;
  draftCount: number | null;
  paid: string;
  /** Under the paid line: what the figures leave out (global read only). */
  footer?: ReactNode;
}

/** The four figures and the paid line, values already decided by the caller. */
function ReceivablesFigures({ overdue, overdueCount, sent, sentCount, saved, savedCount, note, draft, draftCount, paid, footer }: FiguresProps) {
  return (
    <>
    <div data-testid="receivables-grid" className="grid grid-cols-2 gap-x-4 gap-y-5">
      <WidgetFigure
        testId="receivables-overdue"
        label="Overdue"
        alert
        value={overdue}
        sub={invoices(overdueCount)}
      />
      <WidgetFigure
        testId="receivables-sent"
        label="Sent, not yet due"
        value={sent}
        sub={invoices(sentCount)}
      />
      <WidgetFigure
        testId="receivables-saved"
        label="Saved, not sent"
        value={saved}
        sub={
          <>
            {invoices(savedCount)}
            {note && (
              <>
                {' '}
                <span
                  data-testid="receivables-saved-past-due"
                  className="ml-1 whitespace-nowrap rounded-fi-pill border border-black/20 bg-fi-signal-amber px-1.5 py-px text-[0.65rem] font-black uppercase tracking-wide text-fi-signal-ink"
                >
                  {note}
                </span>
              </>
            )}
          </>
        }
      />
      <WidgetFigure
        testId="receivables-draft"
        label="Drafts"
        value={draft}
        sub={invoices(draftCount)}
      />
    </div>
    {/* Money IN, not owed — so a line below a divider, never a fifth tile
        the eye would add to the four above. */}
    <p
      data-testid="receivables-paid"
      className="mt-4 flex items-baseline justify-between gap-3 border-t border-fieldiq-border pt-3 text-xs text-fieldiq-muted"
    >
      <span>Paid this month</span>
      <span className="font-bold tabular-nums text-fieldiq-text">{paid}</span>
    </p>
    {footer}
    </>
  );
}

/**
 * A bucket's money. The API sends an empty list for a bucket with nothing in
 * it, which on a ledger is a real $0.00 — but only when the count agrees; money
 * missing from a non-empty bucket is unknown, so —.
 */
function bucketMoney(b: ReceivablesBucket): string {
  return fmtAmounts(b.amounts, b.count === 0 ? fmtCurrency(0) : '—');
}

/** The amber note over the global read: a subset of saved, never added to it. */
export function globalPastDueNote(saved: ReceivablesBucket, pastDue: ReceivablesBucket): string | null {
  if (pastDue.count <= 0) return null;
  if (pastDue.count === saved.count) return pastDue.count === 1 ? 'past due' : 'all past due';
  return `${pastDue.count} past due · ${fmtAmounts(pastDue.amounts, '—')}`;
}

function GlobalReceivables({ read }: { read: Receivables }) {
  const t = read.total;
  return (
    <ReceivablesFigures
      overdue={bucketMoney(t.overdue)}
      overdueCount={t.overdue.count}
      sent={bucketMoney(t.sent_not_due)}
      sentCount={t.sent_not_due.count}
      saved={bucketMoney(t.saved_not_sent)}
      savedCount={t.saved_not_sent.count}
      note={globalPastDueNote(t.saved_not_sent, t.saved_past_due)}
      draft={bucketMoney(t.draft)}
      draftCount={t.draft.count}
      paid={fmtAmounts(t.paid_this_month, fmtCurrency(0))}
      footer={<MoneyVerticalNotes verticals={read.verticals} testId="receivables-vertical-notes" />}
    />
  );
}

export default function ReceivablesWidget() {
  const { hasVertical } = useAuth();
  const global = hasGlobalMoneyVertical(hasVertical);
  const casing = useOverviewInvoiceDash(!global);
  const book = useReceivables(global);
  const q = global ? book : casing;
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  const d = casing.data;
  const note = d ? pastDueNote(d) : null;

  return (
    <WidgetPanel
      widgetKey="receivables"
      eyebrow="Invoices"
      title="Receivables"
      link={global ? { label: 'Invoices', to: '/finance/invoices' } : { label: 'Invoices', to: '/invoices' }}
    >
      <div className="p-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject="receivables" className="py-6" allowed="admins, office staff, supervisors and dispatchers" />
        ) : global ? (
          book.data === undefined ? (
            <WidgetPending rows={2} />
          ) : book.data === null ? (
            // An envelope with no payload is not an empty ledger.
            <LoadFailureNotice error={new Error('No receivables payload')} kind="unreachable" subject="receivables" className="py-6" />
          ) : (
            <GlobalReceivables read={book.data} />
          )
        ) : !d ? (
          <WidgetPending rows={2} />
        ) : (
          <ReceivablesFigures
            overdue={fmtCurrency(parseMoney(d.balance_overdue))}
            overdueCount={parseCount(d.overdue_count)}
            sent={fmtCurrency(parseMoney(d.balance_sent))}
            sentCount={parseCount(d.sent_count)}
            saved={fmtCurrency(parseMoney(d.balance_finalized))}
            savedCount={parseCount(d.finalized_count)}
            note={note}
            draft={fmtCurrency(parseMoney(d.balance_draft))}
            draftCount={parseCount(d.draft_count)}
            paid={fmtCurrency(parseMoney(d.paid_this_month))}
          />
        )}
      </div>
    </WidgetPanel>
  );
}
